import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { MousePointerClick, TrendingUp, TrendingDown, DollarSign, Target, Eye, Download, ArrowRight, Loader2, BarChart3, Percent } from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from "recharts";
import { exportAdsPDF, filterByDateRange, calculateChange, monthYearToRange, availableYears, previousMonthReport, monthLabel } from "../utils/reportUtils";
import { MonthYearFilter } from "../components/MonthYearFilter";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// KPI Card Component with description
const KPICard = ({ title, value, description, change, icon: Icon, color = "blue", suffix = "", prefix = "", lowerIsBetter = false }) => {
  const colorClasses = {
    blue: { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-100" },
    green: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-100" },
    purple: { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-100" },
    amber: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-100" },
    red: { bg: "bg-red-50", text: "text-red-600", border: "border-red-100" },
    orange: { bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-100" }
  };
  
  const colors = colorClasses[color];
  const isUp = change >= 0;
  const isGood = lowerIsBetter ? change < 0 : change >= 0;

  return (
    <Card className="stat-card">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-3 rounded-xl ${colors.bg}`}>
            <Icon className={`w-6 h-6 ${colors.text}`} strokeWidth={1.5} />
          </div>
          {change !== undefined && change !== null && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              change === 0 ? 'bg-slate-100 text-slate-500' : isGood ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
            }`} title="Variação face ao mês anterior">
              {change !== 0 && (isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />)}
              {isUp && change !== 0 ? "+" : ""}{change.toFixed(1)}%
            </div>
          )}
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {prefix}{value}{suffix}
          </p>
          <p className="text-xs text-slate-400 leading-relaxed mt-2">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const AdsReportsPage = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const { startDate, endDate } = monthYearToRange(selectedMonth, selectedYear);
  const years = availableYears(reports);

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    const filtered = filterByDateRange(reports, startDate, endDate);
    setFilteredReports(filtered);
  }, [reports, startDate, endDate]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/ads`);
      setReports(response.data);
      setFilteredReports(response.data);
    } catch (error) {
      console.error("Error fetching Ads reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (filteredReports.length === 0) {
      toast.error("Sem dados para exportar");
      return;
    }
    
    setExporting(true);
    try {
      const clientId = user?.client_id || filteredReports[0]?.client_id;
      if (!clientId) {
        toast.error("Cliente não identificado");
        return;
      }
      await exportAdsPDF(clientId, startDate, endDate);
      toast.success("PDF exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar PDF:", error);
      toast.error("Erro ao exportar PDF: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSelectedMonth("all");
    setSelectedYear("all");
  };

  const latestReport = filteredReports[0];
  const previousReport = previousMonthReport(reports, latestReport);

  // Calculate ROAS
  const calculateROAS = (r) => {
    if (!r || !r.total_spend || r.total_spend === 0) return 0;
    return (r.revenue || 0) / r.total_spend;
  };

  const pctChange = (field) => {
    if (!latestReport || !previousReport) return null;
    if (!latestReport[field] && !previousReport[field]) return null; // sem dados nos dois meses
    return calculateChange(latestReport[field], previousReport[field]);
  };
  const roasChange = latestReport && previousReport
    ? calculateChange(calculateROAS(latestReport), calculateROAS(previousReport))
    : null;

  const chartData = filteredReports.slice(0, 6).reverse().map(r => ({
    period: r.period_start.slice(5, 10),
    spend: r.total_spend || 0,
    revenue: r.revenue || 0,
    conversions: r.conversions || 0
  }));

  const performanceData = filteredReports.slice(0, 6).reverse().map(r => ({
    period: r.period_start.slice(5, 10),
    impressions: (r.impressions || 0) / 1000,
    clicks: r.clicks || 0
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i}><CardContent className="pt-6"><div className="skeleton h-32" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="ads-reports-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            <div className="p-2 bg-amber-100 rounded-xl">
              <MousePointerClick className="w-7 h-7 text-amber-600" />
            </div>
            Google Ads
          </h1>
          <p className="text-slate-500 mt-2">
            Performance de campanhas pagas e retorno sobre investimento
          </p>
        </div>
        
        {/* Filters & Export */}
        <div className="flex flex-wrap items-end gap-3">
          <MonthYearFilter
            month={selectedMonth}
            year={selectedYear}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
            years={years}
          />
          {(selectedMonth !== "all" || selectedYear !== "all") && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              Limpar
            </Button>
          )}
          <Button 
            onClick={handleExportPDF} 
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="export-pdf-btn"
            disabled={exporting}
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {exporting ? "A exportar..." : "Exportar PDF"}
          </Button>
        </div>
      </div>

      {/* Comparison info */}
      {latestReport && (
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="comparison-info">
          <ArrowRight className="w-4 h-4 text-amber-600" />
          {previousReport ? (
            <span>
              A mostrar <span className="font-medium text-slate-700">{monthLabel(latestReport.period_start)}</span> — variação face a{" "}
              <span className="font-medium text-slate-700">{monthLabel(previousReport.period_start)}</span>
            </span>
          ) : (
            <span>A mostrar {monthLabel(latestReport.period_start)} — sem mês anterior para comparar</span>
          )}
        </div>
      )}

      {latestReport && (
        <>
          {/* Section: Investment & Return */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Investimento e Retorno
              </h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <KPICard
                title="Total Spend"
                value={(latestReport.total_spend || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}
                suffix="€"
                description="Valor total investido em campanhas Google Ads durante o período."
                change={pctChange('total_spend')}
                icon={DollarSign}
                color="amber"
              />
              <KPICard
                title="Revenue Gerado"
                value={(latestReport.revenue || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}
                suffix="€"
                description="Receita total gerada através das campanhas. Base para cálculo do ROAS."
                change={pctChange('revenue')}
                icon={TrendingUp}
                color="green"
              />
              <KPICard
                title="ROAS"
                value={calculateROAS(latestReport).toFixed(2)}
                suffix="x"
                description="Return on Ad Spend. Receita gerada por cada euro investido. Acima de 1x indica lucro."
                change={roasChange}
                icon={BarChart3}
                color="blue"
              />
            </div>
          </div>

          {/* Section: Conversions */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Conversões
              </h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <KPICard
                title="Total Conversões"
                value={(latestReport.conversions || 0).toLocaleString('pt-PT')}
                description="Número de ações valiosas completadas (compras, leads, registos, etc.)."
                change={pctChange('conversions')}
                icon={Target}
                color="green"
              />
              <KPICard
                title="Custo por Conversão"
                value={(latestReport.cost_per_conversion || 0).toFixed(2)}
                suffix="€"
                description="Valor médio gasto para obter cada conversão. Menor é melhor."
                change={pctChange('cost_per_conversion')}
                lowerIsBetter
                icon={DollarSign}
                color="orange"
              />
              <KPICard
                title="CPC Médio"
                value={(latestReport.avg_cpc || 0).toFixed(2)}
                suffix="€"
                description="Custo médio por clique. Indica competitividade das keywords e qualidade dos anúncios."
                change={pctChange('avg_cpc')}
                lowerIsBetter
                icon={MousePointerClick}
                color="purple"
              />
            </div>
          </div>

          {/* Section: Traffic Performance */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Performance de Tráfego
              </h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <KPICard
                title="Impressões"
                value={(latestReport.impressions || 0).toLocaleString('pt-PT')}
                description="Número de vezes que os anúncios foram exibidos aos utilizadores."
                change={pctChange('impressions')}
                icon={Eye}
                color="blue"
              />
              <KPICard
                title="Cliques"
                value={(latestReport.clicks || 0).toLocaleString('pt-PT')}
                description="Quantidade de cliques nos anúncios. Representa o tráfego gerado."
                change={pctChange('clicks')}
                icon={MousePointerClick}
                color="purple"
              />
              <KPICard
                title="CTR"
                value={(latestReport.ctr || 0).toFixed(2)}
                suffix="%"
                description="Click-Through Rate. Percentagem de impressões que resultaram em clique. Indica relevância."
                change={pctChange('ctr')}
                icon={Percent}
                color="amber"
              />
            </div>
          </div>
        </>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <DollarSign className="w-5 h-5 text-amber-600" />
              Spend vs Revenue
            </CardTitle>
            <CardDescription>
              Comparação entre investimento e receita gerada ao longo do tempo
            </CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="period" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => `${value.toFixed(2)}€`}
                  />
                  <Bar dataKey="spend" fill="#F59E0B" name="Spend (€)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="revenue" fill="#10B981" name="Revenue (€)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                Sem dados disponíveis
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Eye className="w-5 h-5 text-blue-600" />
              Impressões e Cliques
            </CardTitle>
            <CardDescription>
              Evolução do alcance e interação com os anúncios
            </CardDescription>
          </CardHeader>
          <CardContent>
            {performanceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={performanceData}>
                  <defs>
                    <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563EB" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#2563EB" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="period" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }} 
                  />
                  <Area type="monotone" dataKey="impressions" stroke="#2563EB" strokeWidth={2} fill="url(#colorImpressions)" name="Impressões (K)" />
                  <Area type="monotone" dataKey="clicks" stroke="#10B981" strokeWidth={2} fill="transparent" name="Cliques" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                Sem dados disponíveis
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reports Table */}
      <Card className="stat-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              Histórico de Relatórios
            </CardTitle>
            <CardDescription>
              Todos os relatórios Google Ads registados
            </CardDescription>
          </div>
          <Badge variant="outline" className="badge-info">
            {filteredReports.length} relatório(s)
          </Badge>
        </CardHeader>
        <CardContent>
          {filteredReports.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Período</TableHead>
                    <TableHead className="text-right">Spend (€)</TableHead>
                    <TableHead className="text-right">Revenue (€)</TableHead>
                    <TableHead className="text-right">ROAS</TableHead>
                    <TableHead className="text-right">Impressões</TableHead>
                    <TableHead className="text-right">Cliques</TableHead>
                    <TableHead className="text-right">CTR</TableHead>
                    <TableHead className="text-right">CPC</TableHead>
                    <TableHead className="text-right">Conv.</TableHead>
                    <TableHead className="text-right">Custo/Conv.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReports.map((report) => (
                    <TableRow key={report.id} data-testid={`ads-report-row-${report.id}`}>
                      <TableCell className="font-medium">
                        {report.period_start} a {report.period_end}
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.total_spend || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}€
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.revenue || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}€
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className={calculateROAS(report) >= 1 ? "badge-success" : "badge-warning"}>
                          {calculateROAS(report).toFixed(2)}x
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.impressions || 0).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.clicks || 0).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.ctr || 0).toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.avg_cpc || 0).toFixed(2)}€
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.conversions || 0).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.cost_per_conversion || 0).toFixed(2)}€
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <MousePointerClick className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Sem relatórios Google Ads para o período selecionado</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Key Insights */}
      {latestReport?.notes && (
        <Card className="stat-card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              Key Insights
            </CardTitle>
            <CardDescription>Observações e recomendações do período</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 leading-relaxed">{latestReport.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdsReportsPage;
